/**
 * /affiliates/approvals — affiliate signup + approval queue page.
 *
 * Declares a TanStack Router route and renders `AffiliateApprovalsPage`. The
 * component is imported by the root-owned `router.tsx` via module
 * augmentation — agents never touch `router.tsx` themselves; the root
 * wire-up cycle picks this route up after the prod merge.
 *
 * Search params reserved for `?status=pending|approved|rejected|all` so
 * operators can deep-link to a specific filter.
 */
import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { AffiliateApprovalsPage } from "@/features/affiliate-approvals/AffiliateApprovalsPage";
import {
	isQueueFilter,
	type QueueFilter,
} from "@/features/affiliate-approvals/api";

// ---------------------------------------------------------------------------
// Route — mounts at `/affiliates/approvals`.
// ---------------------------------------------------------------------------

interface AffiliateApprovalsSearch {
	status?: QueueFilter;
}

export const affiliatesApprovalsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/affiliates/approvals",
	validateSearch: (raw: Record<string, unknown>): AffiliateApprovalsSearch => {
		const status = raw.status;
		if (isQueueFilter(status)) return { status };
		return {};
	},
	component: AffiliatesApprovalsRoute,
});

export default affiliatesApprovalsRoute;

// ---------------------------------------------------------------------------
// Page shell — static breadcrumb + heading + the live feature component.
// ---------------------------------------------------------------------------

export function AffiliatesApprovalsRoute() {
	const { status } = affiliatesApprovalsRoute.useSearch();

	return (
		<div className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-6 pt-6 sm:pt-10">
			<Breadcrumb />
			<AffiliateApprovalsPage initialStatus={status} />
		</div>
	);
}

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">Affiliates</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">Approvals</li>
			</ol>
		</nav>
	);
}
